import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Garden, GardenFilters, PaginatedResponse } from '@types'
import { gardenService } from '@services/gardenService'

function axiosErrText(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  const m = e?.response?.data?.message
  if (typeof m === 'string' && m.trim()) return m
  return e?.message || '请求失败'
}

interface GardenState {
  gardens: Garden[]
  loading: boolean
  error: string | null
  filters: GardenFilters
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  selectedGardens: string[]
  statistics: {
    total: number
    approved: number
    pending: number
    inactive: number
    totalRevenue: number
    totalOrders: number
    totalSubscriptions: number
    averageRating: string
  }
}

const initialState: GardenState = {
  gardens: [],
  loading: false,
  error: null,
  filters: {
    status: 'all',
    farmerId: '',
    location: '',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    pageSize: 10,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  selectedGardens: [],
  statistics: {
    total: 0,
    approved: 0,
    pending: 0,
    inactive: 0,
    totalRevenue: 0,
    totalOrders: 0,
    totalSubscriptions: 0,
    averageRating: '0',
  },
}

// 异步actions
export const fetchGardens = createAsyncThunk(
  'gardens/fetchGardens',
  async (filters: GardenFilters, { rejectWithValue }) => {
    try {
      const response = await gardenService.getGardens(filters)
      return response
    } catch (error: unknown) {
      return rejectWithValue(axiosErrText(error) || '获取菜园列表失败')
    }
  }
)

export const updateGardenStatus = createAsyncThunk(
  'gardens/updateStatus',
  async ({ id, status }: { id: string; status: 'approved' | 'pending' | 'inactive' }, { rejectWithValue }) => {
    try {
      await gardenService.updateGardenStatus(id, status)
      return { id, status }
    } catch (error: unknown) {
      return rejectWithValue(axiosErrText(error) || '更新菜园状态失败')
    }
  }
)

export const batchUpdateStatus = createAsyncThunk(
  'gardens/batchUpdateStatus',
  async ({ ids, status }: { ids: string[]; status: 'approved' | 'pending' | 'inactive' }, { rejectWithValue }) => {
    try {
      await gardenService.batchUpdateStatus(ids, status)
      return { ids, status }
    } catch (error: unknown) {
      return rejectWithValue(axiosErrText(error) || '批量更新菜园状态失败')
    }
  }
)

export const fetchStatistics = createAsyncThunk(
  'gardens/fetchStatistics',
  async (filters: GardenFilters, { rejectWithValue }) => {
    try {
      const statistics = await gardenService.getGardenStatistics(filters)
      return statistics
    } catch (error: unknown) {
      return rejectWithValue(axiosErrText(error) || '获取菜园统计失败')
    }
  }
)

const gardenSlice = createSlice({
  name: 'gardens',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<GardenFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setPagination: (state, action: PayloadAction<Partial<{ page: number; pageSize: number; total: number }>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setSelectedGardens: (state, action: PayloadAction<string[]>) => {
      state.selectedGardens = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearSelected: (state) => {
      state.selectedGardens = []
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取菜园列表
      .addCase(fetchGardens.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchGardens.fulfilled, (state, action: PayloadAction<PaginatedResponse<Garden>>) => {
        state.loading = false
        state.gardens = action.payload.data
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
        }
        state.error = null
      })
      .addCase(fetchGardens.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // 更新菜园状态
      .addCase(updateGardenStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload
        const garden = state.gardens.find(garden => garden.id === id)
        if (garden) {
          garden.status = status
        }
      })
      
      // 批量更新状态
      .addCase(batchUpdateStatus.fulfilled, (state, action) => {
        const { ids, status } = action.payload
        state.gardens.forEach(garden => {
          if (ids.includes(garden.id)) {
            garden.status = status
          }
        })
        state.selectedGardens = []
      })
      
      // 获取统计
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.error = (action.payload as string) || '获取统计失败'
      })
  },
})

export const { setFilters, setPagination, setSelectedGardens, clearError, clearSelected } = gardenSlice.actions
export default gardenSlice.reducer